import React, {useEffect, useMemo, useRef, useState} from 'react';
import Api from '../../components/api/api';
import {useToasts} from 'react-bootstrap-toasts';

import 'filepond/dist/filepond.min.css';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Navigations} from "../../components/navigations";
import {
  BoardButtonsBox,
  BoardCardsWrapper,
  BoardEmptyFiles,
  BoardEmptySubTitle, BoardEmptySubTitleHelper,
  BoardEmptyTitle, BoardEmptyTitlesBox,
  BoardWrapper
} from "./dashboard.styled";
import {BoardCard} from "../../components/ui/BoardCard";
import {BoardCardsActive} from "../../components/ui/BoardCard/BoardCard.styled";
import {BoardNewCard} from "../../components/ui/BoardNewCard";
import {FooterBoard} from "../../components/footerBoard";
import {UploadModal, UploadModalRef} from "../../components/uploadModal";
import {UiBoardButtonUp} from "../../components/ui/BoardButtonUp";
import {UiAddButton} from "../../components/ui/addButton";
import {DownloadModal} from "../../components/downloadModal";
import {Box, CircularProgress} from "@mui/material";

const api = Api();

export interface FileParams {
  page: number
  limit: number
  total: number
  search: string
  order: string
}

export const DashboardPage = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [filesParams, setFileParams] = useState<FileParams>({
    page: 1,
    limit: 50,
    total: 0,
    search: '',
    order: 'created_at desc',
  });
  const [pushLimit, setPushLimit] = useState<string>('50');
  const [downloadFileData, setDownloadFileData] = useState<{ fileId: string; fileName: string } | undefined>();
  const [showButtons, setShowButtons] = useState<boolean>(false);

  const modalRef = useRef<UploadModalRef>(null);
  const modalDownloadRef = useRef<UploadModalRef>(null);

  const toasts = useToasts();
  const queryClient = useQueryClient();

  const handleOpenEnctyptFileModal = () => {
    modalRef.current?.openModal();
  };

  const { mutate: DownloadMutate } = useMutation({
    mutationKey: ['downloadFile'],
    mutationFn: async ({ fileId, fileName }: { fileId: string; fileName: string }) => {
      const buffer = await api.downloadFile(fileId);
      await api.downloadBlob(buffer, fileName);
    },
    onError: (resp: ApiResponse | any) => {
      api.errorHandler(resp, toasts);
    },
  });

  const {data: files, isFetching} = useQuery({
    queryKey: ['getFiles', filesParams, filesParams.search],
    initialData: [],
    queryFn: async () => {
      const {data} = await api.getFiles(filesParams.page, filesParams.limit, filesParams.order, filesParams.search);

      return data || [];
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollTop > 200) {
        setShowButtons(true);
      }

      if (scrollTop === 0) {
        setShowButtons(false);
      }

      if (scrollTop + clientHeight >= scrollHeight - 150) {
        setShowButtons(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleLoadMore = () => {
    const additional = parseInt(pushLimit, 10);
    if (!isNaN(additional)) {
      setFileParams((prev) => ({
        ...prev,
        limit: prev.limit + additional,
      }));
    }
  };

  const deleteFile = async (fileId: string) => {
    try {
      await api.deleteFile(fileId);
      await queryClient.invalidateQueries({queryKey: ['getFiles']});
    } catch (err: any) {
      console.error('Error deleting file:', err.message);
    }
  };

  const downloadFile = (fileId: string, fileName: string) => {
    const fileNameParts = fileName.split('.');
    const format = fileNameParts[fileNameParts.length - 1];

    if (format === 'enc') {
      setDownloadFileData({ fileId, fileName });
      modalDownloadRef.current?.openModal();
    } else {
      DownloadMutate({ fileId, fileName });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const notSelectedFiles = useMemo(() => {
    return filesParams.search.length > 0 && files.length === 0;
  }, [filesParams.search, files]);

  return (
    <BoardWrapper>
      <Navigations setFileParams={setFileParams} totalFiles={files.length}/>
      <BoardCardsWrapper>
        {isFetching ? <CircularProgress sx={{
            position: 'absolute',
            right: '40%',
            top: '30%'
          }} size={300}/> :
          <>
            {files.length > 0 ?
              <>
                <BoardNewCard uploadFile={handleOpenEnctyptFileModal}/>
                {files.map((file, index) => (
                  <BoardCardsActive
                    key={index}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <BoardCard
                      active={hoveredIndex === index}
                      title={file.name}
                      subtitle={file.size}
                      status={file.status}
                      hash={file.hash}
                      downloadFile={() => downloadFile(file.hash, file.name)}
                      deleteFile={() => deleteFile(file.id)}
                    />
                  </BoardCardsActive>
                ))}
              </>
              :
              <BoardEmptyFiles>
                <BoardEmptyTitlesBox>
                  {notSelectedFiles ?
                    <>
                      <BoardEmptyTitle>Found nothing by</BoardEmptyTitle>
                      <Box display="flex" alignItems="center" justifyContent="center">
                        <BoardEmptySubTitleHelper>"</BoardEmptySubTitleHelper>
                        <BoardEmptySubTitle>{`${filesParams.search}`}</BoardEmptySubTitle>
                        <BoardEmptySubTitleHelper>"</BoardEmptySubTitleHelper>
                      </Box>
                    </>
                    :
                    <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                      <BoardEmptyTitle>Click here or drag & drop</BoardEmptyTitle>
                      <BoardEmptySubTitle>to upload your first file</BoardEmptySubTitle>
                      <UiAddButton aboutFunction={handleOpenEnctyptFileModal}/>
                    </Box>
                  }
                </BoardEmptyTitlesBox>
              </BoardEmptyFiles>
            }
          </>
        }
      </BoardCardsWrapper>
      {showButtons &&
          <BoardButtonsBox>
              <UiAddButton aboutFunction={handleOpenEnctyptFileModal}/>
              <UiBoardButtonUp aboutFunction={scrollToTop}/>
          </BoardButtonsBox>
      }
      <UploadModal ref={modalRef}/>
      <DownloadModal
        ref={modalDownloadRef}
        fileId={downloadFileData?.fileId}
        fileName={downloadFileData?.fileName}
      />
      {files.length > 0 && (
        <FooterBoard
          pushLimit={pushLimit}
          setPushLimit={setPushLimit}
          handleLoadMore={handleLoadMore}
          uploadFile={handleOpenEnctyptFileModal}
        />
      )}
    </BoardWrapper>
  );
};
